import { useMemo } from 'react'

import { Search, X } from 'lucide-react'

import { exportSurveysReport, type ReportFilters } from '../../../api/reportsApi'
import { SURVEY_REPORT_STATUSES, type SiteStatus, type SurveyReportStatus , type GisScope, type GisSite } from '../../../api/types'
import { useSurveysReport } from '../../../hooks/useSurveysReport'
import {
  COVERAGE_GROUP_OPTIONS,
  filterSites,
  siteOfficerKey,
  type SiteFilters,
 type CoverageListMode } from '../../../lib/coverage-board'
import { SITE_STATUS_META } from '../../../lib/lens'
import { prettyName } from '../../../lib/mandal-name'
import { useGisCoverageView } from '../../../stores/gisCoverageViewStore'
import {
  buildFacetOptions,
  FilterDropdown,
  MultiFilterDropdown,
  OptionRow,
} from '../../ui/FilterDropdown'
import { GroupSelect } from '../../ui/GroupSelect'
import { SegmentedControl } from '../../ui/SegmentedControl'
import { FilterChipBar } from '../FilterChipBar'
import { ExportButton } from '../reports/ExportButton'

const STATUSES: SiteStatus[] = ['overdue', 'never_surveyed', 'in_progress', 'surveyed']

const MODE_ITEMS: { key: CoverageListMode; label: string }[] = [
  { key: 'sites', label: 'Sites' },
  { key: 'officers', label: 'Officers' },
  { key: 'surveys', label: 'Surveys' },
]

const SURVEY_LABEL: Record<SurveyReportStatus, string> = { pending: 'Pending', missed: 'Missed' }

interface CoverageToolbarProps {
  scope: GisScope
  /** The window's full site list (options + counts derive from it). */
  sites: GisSite[]
  activeMandals: readonly string[]
  onSelectMandal: (name: string) => void
  onClearMandal: () => void
}

/** The Surveys view's controls: Pending/Missed toggle (live counts) + the xlsx export. Reads the
 *  same report query the table uses (deduped by the shared queryOptions), so counts always match. */
function SurveysControls({ filters }: { filters: ReportFilters }) {
  const surveyStatus = useGisCoverageView((s) => s.surveyStatus)
  const setSurveyStatus = useGisCoverageView((s) => s.setSurveyStatus)
  const { data } = useSurveysReport(filters, surveyStatus)
  const counts = data?.pages[0]?.counts
  const rowCount = data?.pages.flatMap((p) => p.rows).length ?? 0
  return (
    <>
      <SegmentedControl<SurveyReportStatus>
        aria-label="Survey report status"
        size="sm"
        track="a"
        shape="rounded"
        items={SURVEY_REPORT_STATUSES.map((s) => ({
          key: s,
          label: counts ? `${SURVEY_LABEL[s]} ${counts[s]}` : SURVEY_LABEL[s],
        }))}
        value={surveyStatus}
        onChange={setSurveyStatus}
      />
      <ExportButton
        onExport={() => exportSurveysReport(filters, surveyStatus)}
        disabled={rowCount === 0}
      />
    </>
  )
}

/**
 * The Coverage page's full-width control row (V5 layout, the Coverage twin of `IssuesToolbar`).
 * Sites/Officers views: LEFT — bounded search + Status/Officer/Mandal dropdowns; RIGHT — Group
 * (Sites view only) + the Sites|Officers|Surveys switch. Surveys view: the filter strip yields to
 * the Pending/Missed toggle + Export (the only controls that apply to the server report). Applied
 * filters render as a chips line; everything writes `gisCoverageViewStore` (mandal → the URL), so
 * the map dots, chips and list react in lockstep.
 */
export function CoverageToolbar({ scope, sites, activeMandals, onSelectMandal, onClearMandal }: CoverageToolbarProps) {
  const v = useGisCoverageView
  const statusFilter = v((s) => s.statusFilter)
  const officerFilter = v((s) => s.officerFilter)
  const query = v((s) => s.query)
  const groupBy = v((s) => s.groupBy)
  const listMode = v((s) => s.listMode)
  const toggleStatus = v((s) => s.toggleStatusFilter)
  const toggleOfficer = v((s) => s.toggleOfficerFilter)
  const setQuery = v((s) => s.setQuery)
  const setGroupBy = v((s) => s.setGroupBy)
  const setListMode = v((s) => s.setListMode)
  const clearFilters = v((s) => s.clearFilters)

  const filters: SiteFilters = { statuses: statusFilter, officers: officerFilter, mandal: activeMandals, query }

  // Option counts exclude their OWN facet (options stay switchable) but respect every other filter.
  const options = useMemo(() => {
    const except = (patch: Partial<SiteFilters>): GisSite[] => filterSites(sites, { ...filters, ...patch })
    const statusBase = except({ statuses: [] })
    const statusCounts = Object.fromEntries(STATUSES.map((s) => [s, 0])) as Record<SiteStatus, number>
    for (const s of statusBase) {
      statusCounts[s.status] += 1
    }
    return {
      status: statusCounts,
      officers: buildFacetOptions(sites, except({ officers: [] }), siteOfficerKey),
      mandals: buildFacetOptions(sites, except({ mandal: [] }), (s) => s.mandal, prettyName),
    }
     
  }, [sites, statusFilter, officerFilter, query, activeMandals])

  const clearAll = (): void => {
    clearFilters()
    onClearMandal()
  }

  const chips = [
    ...statusFilter.map((st) => ({ label: `Status: ${SITE_STATUS_META[st].label}`, clear: () => toggleStatus(st) })),
    ...officerFilter.map((o) => ({ label: `Officer: ${o}`, clear: () => toggleOfficer(o) })),
    ...activeMandals.map((m) => ({ label: prettyName(m), clear: () => onSelectMandal(m) })),
  ]

  const reportFilters: ReportFilters = {
    scope,
    mandal: activeMandals.length === 1 ? activeMandals[0] : undefined,
  }
  const surveysMode = listMode === 'surveys'

  return (
    <div className="border-b border-gis-line bg-white">
      <div className="flex flex-wrap items-center gap-2 px-3.5 py-[7px]">
        {surveysMode ? (
          <SurveysControls filters={reportFilters} />
        ) : (
          <>
            {/* Search — bounded width; ✕ clears. Matches site / officer / mandal. */}
            <label className="flex h-[30px] w-[220px] min-w-[170px] items-center gap-[7px] rounded-[9px] border border-gis-line bg-white px-[10px] transition-colors focus-within:border-[#c7d2ea] focus-within:bg-[#fbfcfe]">
              <Search className="h-[13px] w-[13px] flex-none text-gis-muted" aria-hidden />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search sites…"
                aria-label="Search sites"
                className="min-w-0 flex-1 bg-transparent text-[12px] text-gis-ink outline-none placeholder:text-gis-muted [&::-webkit-search-cancel-button]:hidden"
              />
              {query !== '' && (
                <button type="button" aria-label="Clear search" onClick={() => setQuery('')}
                  className="grid h-4 w-4 flex-none place-items-center rounded-full text-gis-muted transition-colors hover:bg-gis-track hover:text-gis-ink">
                  <X className="h-[11px] w-[11px]" />
                </button>
              )}
            </label>

            <FilterDropdown label="Status" activeCount={statusFilter.length}>
              {STATUSES.map((st) => (
                <OptionRow
                  key={st}
                  checked={statusFilter.includes(st)}
                  label={SITE_STATUS_META[st].label}
                  count={options.status[st]}
                  swatch={SITE_STATUS_META[st].color}
                  onToggle={() => toggleStatus(st)}
                />
              ))}
            </FilterDropdown>
            <MultiFilterDropdown label="Officer" options={options.officers} active={officerFilter} onToggle={toggleOfficer} />
            <MultiFilterDropdown label="Mandal" options={options.mandals} active={activeMandals} onToggle={onSelectMandal} />
          </>
        )}

        <div className="ml-auto flex flex-none items-center gap-2">
          {listMode === 'sites' && (
            <GroupSelect options={COVERAGE_GROUP_OPTIONS} value={groupBy} noneValue="none" onChange={setGroupBy} />
          )}
          <SegmentedControl<CoverageListMode>
            aria-label="Coverage view"
            size="sm"
            track="a"
            shape="rounded"
            items={MODE_ITEMS}
            value={listMode}
            onChange={setListMode}
          />
        </div>
      </div>

      {!surveysMode && chips.length > 0 && (
        <div className="border-t border-gis-line-2 px-3.5 py-[5px]">
          <FilterChipBar chips={chips} onClearAll={clearAll} />
        </div>
      )}
    </div>
  )
}
